import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import OriginalVACharacter from '../assets/OriginalVACharacter';

const Hero = () => {
  const parallaxRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!parallaxRef.current) return;
      
      const elements = parallaxRef.current.querySelectorAll('.parallax');
      
      elements.forEach(el => {
        const speed = el.getAttribute('data-speed') || 0.05;
        const x = (window.innerWidth - e.pageX * speed) / 100;
        const y = (window.innerHeight - e.pageY * speed) / 100;
        
        el.style.transform = `translateX(${x}px) translateY(${y}px)`;
      });
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <section className="relative min-h-screen bg-gradient-primary overflow-hidden" ref={parallaxRef}>
      <Navbar />
      
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-primary-200 opacity-30 blur-3xl parallax" data-speed="0.03"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-primary-300 opacity-20 blur-3xl parallax" data-speed="0.05"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 rounded-full bg-accent-300 opacity-10 blur-3xl parallax" data-speed="0.07"></div>
      </div>
      
      <div className="container mx-auto px-4 pt-32 md:pt-40 pb-20 relative z-10">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-12 md:mb-0">
            <motion.h1 
              className="text-primary-700 font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Hire Dedicated Virtual Assistants for your business from as low as <span className="text-accent-600">$4 an Hour</span>
            </motion.h1>
            
            <motion.p 
              className="text-neutral-700 text-xl mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <span className="font-semibold">Save 20+ Hours/Week with Expert VAs</span> – Starting at <span className="text-accent-600">$4/Hour</span>. Results Guaranteed.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <a 
                href="https://calendly.com/adam-cothes/va-consultation"
                target="_blank"
                rel="noopener noreferrer" 
                className="btn btn-primary mr-4"
              >
                Book a Call
              </a>
              <a href="#services" className="btn btn-secondary">
                See VA Services
              </a>
            </motion.div>
          </div>
          
          <div className="md:w-1/2 relative">
            <motion.div
              className="relative z-10"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="relative">
                <OriginalVACharacter className="w-full h-auto" />
              </div>
              
              {/* Floating elements */}
              <div className="absolute -right-5 -bottom-5 bg-white rounded-xl shadow-normal p-4 animate-float">
                <div className="flex items-center">
                  <div className="text-accent-600 mr-3">
                    <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-neutral-800 font-semibold">20+ Hours Saved</p>
                    <p className="text-neutral-500 text-sm">Every Week</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -left-5 top-10 bg-white rounded-xl shadow-normal p-4 animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center">
                  <div className="text-accent-600 mr-3">
                    <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"></path>
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-neutral-800 font-semibold">From <span className="text-accent-600">$4/Hour</span></p>
                    <p className="text-neutral-500 text-sm">Expert VAs</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;